/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ChannelDeliveryOption,
    DeliveryModeTypeFilter as RetailProxyDeliveryModeTypeFilter,
    FeatureState,
    OrgUnitAvailability,
    OrgUnitAvailabilitySearchCriteria,
    OrgUnitLocation,
    SearchArea,
    StoreHours
} from '@msdyn365-commerce/retail-proxy';
import {
    getChannelDeliveryOptionsAsync,
    getOrgUnitLocationsByAreaAcrossCompaniesAsync,
    getStoreHoursAsync,
    searchProductAvailabilityAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';

import { IActionContext } from '@msdyn365-commerce/core';
import { FeatureStateInput, getFeatureStateActionDataAction } from './get-feature-state';
import { IFullOrgUnitAvailability } from './utilities/full-org-unit-availability';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

const ALL_DATA_AREAS = ['*'];
const DEFAULT_DELIVERY_MODE_FILTER = RetailProxyDeliveryModeTypeFilter.None;
const PICKUP_DELIVERY_MODE_FILTER = RetailProxyDeliveryModeTypeFilter.Pickup;
const EARTH_RADIUS_KILOMETERS = 6371;
const KILOMETERS_TO_MILES = 0.621371;

/**
 * Input shared by the product-scoped and location-only Cross-LE Store Selector paths.
 */
export class GetCrossEntityStoreLocationsInput {
    public productId?: number;

    public latitude?: number;

    public longitude?: number;

    public radius?: number;

    public distanceUnitValue?: number;

    public ignoreLocation?: boolean;

    public constructor(
        productId?: number,
        latitude?: number,
        longitude?: number,
        radius?: number,
        distanceUnitValue?: number,
        ignoreLocation?: boolean
    ) {
        this.productId = productId;
        this.latitude = latitude;
        this.longitude = longitude;
        this.radius = radius;
        this.distanceUnitValue = distanceUnitValue;
        this.ignoreLocation = ignoreLocation;
    }
}

const getLocationKey = (dataAreaId: string | undefined, locationId: string | undefined): string | undefined =>
    dataAreaId && locationId ? `${dataAreaId}::${locationId}` : undefined;

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Adds a calculated distance when the across-companies API omits it.
 * Distances returned by the API remain authoritative.
 * @param locations - Store locations returned by the across-companies API.
 * @param input - Search origin and preferred distance unit.
 * @returns Locations with distance populated where coordinates are available.
 */
const populateMissingDistances = (locations: OrgUnitLocation[], input: GetCrossEntityStoreLocationsInput): OrgUnitLocation[] => {
    if (input.ignoreLocation || input.radius === undefined || input.latitude === undefined || input.longitude === undefined) {
        return locations;
    }

    const searchLatitude = input.latitude;
    const searchLongitude = input.longitude;

    return locations.map(location => {
        if (location.Distance !== undefined || location.Latitude === undefined || location.Longitude === undefined) {
            return location;
        }

        const latitudeDelta = toRadians(location.Latitude - searchLatitude);
        const longitudeDelta = toRadians(location.Longitude - searchLongitude);
        const originLatitude = toRadians(searchLatitude);
        const locationLatitude = toRadians(location.Latitude);
        const haversine =
            Math.sin(latitudeDelta / 2) ** 2 + Math.cos(originLatitude) * Math.cos(locationLatitude) * Math.sin(longitudeDelta / 2) ** 2;
        const distanceInKilometers = 2 * EARTH_RADIUS_KILOMETERS * Math.asin(Math.sqrt(haversine));
        const distance = input.distanceUnitValue === 1 ? distanceInKilometers : distanceInKilometers * KILOMETERS_TO_MILES;

        return { ...location, Distance: distance };
    });
};

/**
 * Merges area-filtered locations with product availability without losing location distance data.
 * @param locations - Locations returned by GetOrgUnitLocationsByAreaAcrossCompanies.
 * @param availabilities - Product availability returned by SearchProductAvailability.
 * @returns Full availability records ready for Store Selector enrichment.
 */
export const mergeCrossEntityLocationsWithAvailability = (
    locations: OrgUnitLocation[],
    availabilities: OrgUnitAvailability[]
): IFullOrgUnitAvailability[] => {
    const availabilityByInventoryLocation = new Map<string, OrgUnitAvailability>();
    const availabilityByOrgUnit = new Map<string, OrgUnitAvailability>();
    availabilities.forEach(availability => {
        const location = availability.OrgUnitLocation;
        const inventoryLocationKey = getLocationKey(location?.DataAreaId, location?.InventoryLocationId);
        const orgUnitKey = getLocationKey(location?.DataAreaId, location?.OrgUnitNumber);

        if (inventoryLocationKey) {
            availabilityByInventoryLocation.set(inventoryLocationKey, availability);
        }

        if (orgUnitKey) {
            availabilityByOrgUnit.set(orgUnitKey, availability);
        }
    });

    return locations.map(location => {
        const inventoryLocationKey = getLocationKey(location.DataAreaId, location.InventoryLocationId);
        const orgUnitKey = getLocationKey(location.DataAreaId, location.OrgUnitNumber);
        const availability =
            (inventoryLocationKey ? availabilityByInventoryLocation.get(inventoryLocationKey) : undefined) ??
            (orgUnitKey ? availabilityByOrgUnit.get(orgUnitKey) : undefined);
        return {
            OrgUnitAvailability: {
                ...availability,
                OrgUnitLocation: location,
                ItemAvailabilities: availability?.ItemAvailabilities ?? []
            }
        };
    });
};

/**
 * Gets Store Selector locations across all visible legal entities.
 * @param input - Search input.
 * @param context - Action context.
 * @returns Cross-company locations.
 */
async function getLocationsAcrossCompanies(
    input: GetCrossEntityStoreLocationsInput,
    context: IActionContext,
    deliveryModeTypeFilter: RetailProxyDeliveryModeTypeFilter
): Promise<OrgUnitLocation[]> {
    const hasLocation = input.radius !== undefined && input.latitude !== undefined && input.longitude !== undefined;

    if (!hasLocation && !input.ignoreLocation) {
        return [];
    }

    const searchArea: SearchArea = {
        Latitude: input.latitude ?? 0,
        Longitude: input.longitude ?? 0,
        Radius: input.radius ?? 0,
        DistanceUnitValue: input.distanceUnitValue ?? 0
    };

    const locations = await getOrgUnitLocationsByAreaAcrossCompaniesAsync(
        {
            callerContext: context,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
        },
        searchArea,
        ALL_DATA_AREAS,
        deliveryModeTypeFilter
    );

    return populateMissingDistances(locations ?? [], input);
}

/**
 * Gets the multiple-pickup feature state used by the legacy Store Selector experience.
 * @param context - Action context.
 * @returns Whether multiple pickup modes are enabled.
 */
async function isMultiplePickupModeEnabled(context: IActionContext): Promise<boolean> {
    const featureState: FeatureState[] = await getFeatureStateActionDataAction(new FeatureStateInput(), context);
    return featureState?.find(item => item.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature')?.IsEnabled ?? false;
}

/**
 * Gets pickup delivery options for the supplied channels.
 * @param locations - Store locations.
 * @param context - Action context.
 * @returns Delivery options by channel.
 */
async function getLocationPickupDeliveryModes(locations: OrgUnitLocation[], context: IActionContext): Promise<ChannelDeliveryOption[]> {
    if (!(await isMultiplePickupModeEnabled(context))) {
        return [];
    }

    const channelIds = locations.map(location => location.ChannelId).filter((channelId): channelId is number => channelId !== undefined);

    if (channelIds.length === 0) {
        return [];
    }

    return getChannelDeliveryOptionsAsync(
        {
            callerContext: context,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
        },
        channelIds,
        4
    );
}

/**
 * Preserves Store Selector's hours and pickup-mode behavior for Cross-LE results.
 * @param stores - Store records to enrich.
 * @param context - Action context.
 * @returns Enriched store records.
 */
async function enrichCrossEntityStores(stores: IFullOrgUnitAvailability[], context: IActionContext): Promise<IFullOrgUnitAvailability[]> {
    const locations = stores
        .map(store => store.OrgUnitAvailability?.OrgUnitLocation)
        .filter((location): location is OrgUnitLocation => location !== undefined);
    let pickupDeliveryModes: ChannelDeliveryOption[] = [];

    try {
        pickupDeliveryModes = await getLocationPickupDeliveryModes(locations, context);
    } catch (error) {
        context.telemetry.error((error as Error).message);
        context.telemetry.debug('[CrossEntityStoreLocations] error getting channel delivery options');
    }

    return Promise.all(
        stores.map(async store => {
            const location = store.OrgUnitAvailability?.OrgUnitLocation;
            if (!location?.OrgUnitNumber) {
                return store;
            }

            let storeHours: StoreHours | undefined;
            try {
                storeHours = await getStoreHoursAsync({ callerContext: context }, location.OrgUnitNumber);
            } catch (error) {
                context.telemetry.error((error as Error).message);
                context.telemetry.debug('[CrossEntityStoreLocations] error getting store hours');
            }

            return {
                ...store,
                StoreHours: storeHours,
                OrgUnitPickUpDeliveryModes: pickupDeliveryModes.find(option => option.ChannelId === location.ChannelId)
            };
        })
    );
}

/**
 * Gets product availability for nearby stores across all visible legal entities.
 * @param input - Product and area search input.
 * @param context - Action context.
 * @returns Product availability with legacy Store Selector enrichment.
 */
export async function getCrossEntityFullAvailableInventoryNearby(
    input: GetCrossEntityStoreLocationsInput,
    context: IActionContext
): Promise<IFullOrgUnitAvailability[]> {
    if (!input.productId) {
        return [];
    }

    try {
        const locations = await getLocationsAcrossCompanies(input, context, PICKUP_DELIVERY_MODE_FILTER);
        const searchCriteria: OrgUnitAvailabilitySearchCriteria = { DataAreaIds: ALL_DATA_AREAS };
        const availabilities = await searchProductAvailabilityAsync(
            {
                callerContext: context,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
            },
            input.productId,
            searchCriteria
        );

        return enrichCrossEntityStores(mergeCrossEntityLocationsWithAvailability(locations ?? [], availabilities ?? []), context);
    } catch (error) {
        context.telemetry.error((error as Error).message);
        context.telemetry.debug('[CrossEntityStoreLocations] error getting product availability');
        return [];
    }
}

/**
 * Gets location-only Store Selector results across all visible legal entities.
 * @param input - Area search input.
 * @param context - Action context.
 * @returns Locations with legacy Store Selector enrichment.
 */
export async function getCrossEntityOrgUnitLocationsByArea(
    input: GetCrossEntityStoreLocationsInput,
    context: IActionContext
): Promise<IFullOrgUnitAvailability[]> {
    try {
        const locations = await getLocationsAcrossCompanies(input, context, DEFAULT_DELIVERY_MODE_FILTER);
        const stores = (locations ?? []).map(location => ({
            OrgUnitAvailability: { OrgUnitLocation: location }
        }));
        return enrichCrossEntityStores(stores, context);
    } catch (error) {
        context.telemetry.error((error as Error).message);
        context.telemetry.debug('[CrossEntityStoreLocations] error getting locations');
        return [];
    }
}
